/**
 * @file SkuStorageFields.tsx
 * @description Складские поля фасовки: ячейка по умолчанию и порог «мало»
 * (phase19.14, W14 + W9).
 *
 * Вынесены из `SkuEditDialog` отдельной секцией с контрактом `value/onChange`
 * (образец `CarrierMainFields`): редактор SKU и так на пределе размера, а эти
 * два поля — про склад, а не про карточку товара.
 *
 * Ячейки грузятся по всему магазину и группируются по складам: сервер требует
 * лишь активную ячейку ЭТОГО магазина (`sku_default_bin_not_found`), склад в
 * подсказке «куда положить» не фиксируется.
 */

import { useEffect, useState } from "react";

import { Label } from "../../../../components/ui/label";
import { binsApi, type StorageBinDto } from "../../../../lib/api-bins";
import { warehousesApi, type WarehouseDto } from "../../../../lib/api-wms";
import { qtyInputProps } from "../../../../lib/qty-input";

/** Порог «мало» меряется фасовками — целые штуки (реестр `qty-input`). */
const MIN_STOCK_FIELD = qtyInputProps("шт");

export interface SkuStorageValue {
  /** Пустая строка — ячейка не выбрана (сбрасывает `defaultBinId` в null). */
  defaultBinId: string;
  /** Пустая строка — порог не задан (подсветка «мало» выключена). */
  minStock: string;
}

interface Props {
  value: SkuStorageValue;
  onChange: (next: SkuStorageValue) => void;
  disabled?: boolean;
}

const nativeSelectCls =
  "flex h-10 w-full rounded-md bg-input px-3 py-2 text-sm text-on-surface outline-none focus-visible:bg-surface-lowest focus-visible:[box-shadow:inset_0_-2px_0_var(--primary)]";

export function SkuStorageFields({ value, onChange, disabled }: Props) {
  const [bins, setBins] = useState<StorageBinDto[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);

  useEffect(() => {
    let cancelled = false;
    // Ячейки всех складов: склад фасовки заранее неизвестен, группируем ниже.
    // Сводка содержимого здесь не нужна — это выбор адреса по умолчанию.
    Promise.all([binsApi.list({ withSummary: false }), warehousesApi.list()])
      .then(([b, w]) => {
        if (cancelled) return;
        setBins(b);
        setWarehouses(w);
      })
      .catch(() => {
        // Справочник ячеек недоступен — поле остаётся пустым селектом, форма
        // фасовки при этом обязана сохраняться.
        if (!cancelled) setBins([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const byWarehouse = warehouses
    .map((w) => ({ warehouse: w, bins: bins.filter((b) => b.warehouseId === w.id) }))
    .filter((g) => g.bins.length > 0);

  return (
    <div>
      <div className="label-editorial mb-3">Склад</div>
      <div className="flex gap-3">
        <div className="flex-[1.4] flex flex-col gap-1.5">
          <Label htmlFor="sku-bin">Ячейка по умолчанию</Label>
          <select
            id="sku-bin"
            value={value.defaultBinId}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, defaultBinId: e.target.value })}
            className={nativeSelectCls}
          >
            <option value="">— не задана —</option>
            {byWarehouse.map((group) => (
              <optgroup key={group.warehouse.id} label={group.warehouse.name}>
                {group.bins.map((bin) => (
                  <option key={bin.id} value={bin.id}>
                    {bin.code}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="text-xs text-on-surface-variant">
            Куда класть при приёмке. Новая партия унаследует этот адрес.
          </p>
        </div>
        <div className="flex-1 flex flex-col gap-1.5">
          <Label htmlFor="sku-min-stock">Мин. остаток, шт</Label>
          {/* Порог сравнивается с остатком, а остаток — это пачки на полке:
              стрелка шагает на штуку, дробных порогов не бывает. */}
          <input
            id="sku-min-stock"
            type="number"
            step={MIN_STOCK_FIELD.step}
            inputMode={MIN_STOCK_FIELD.inputMode}
            min={0}
            value={value.minStock}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, minStock: e.target.value })}
            className="flex h-10 w-full rounded-md bg-input px-3 py-2 text-sm text-on-surface outline-none focus-visible:bg-surface-lowest focus-visible:[box-shadow:inset_0_-2px_0_var(--primary)]"
          />
          <p className="text-xs text-on-surface-variant">
            Пусто — подсветка «мало» только при нуле.
          </p>
        </div>
      </div>
    </div>
  );
}
